import React from "react";
import { motion, TargetAndTransition, Transition } from "motion/react";
import { useDownloadContext } from "@/contexts/DownloadContext";
import { adjustTracking } from "@/services/adjust";
import styles from "./styles.module.scss";

interface InfoOverlayProps {
  close: () => void;
  still?: string;
  fellou?: string;
  variant?: string;
}

/**
 * 信息覆盖层组件
 * 用于显示下载确认对话框、菜单等信息
 */
const InfoOverlay: React.FC<InfoOverlayProps> = ({ close, still = "", fellou = "", variant = "download" }) => {
  const { downloadInfo, isLoading, getMacArmResource, getMacIntelResource, getWindowsResource } = useDownloadContext();

  // 下载文件函数
  const downloadFile = (url: string, platform: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "download";
    link.click();
    const channel = localStorage.getItem("utm_source");

    // 追踪下载
    fetch("https://admin.fellou.ai/api/download/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ platform, channel }),
    }).catch((error) => console.error("Error tracking download:", error));

    close();
  };

  // 获取资源
  const macArmResource = getMacArmResource();
  const macIntelResource = getMacIntelResource();
  const windowsResource = getWindowsResource();

  // 获取平台信息
  const macInfo = downloadInfo.find((item) => item.type === "mac");
  const windowsInfo = downloadInfo.find((item) => item.type === "windows");

  // Mac ARM下载处理
  const handleMacArmDownload = () => {
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    if (!macArmResource) {
      console.error("Mac ARM resource not available");
      return;
    }

    // 追踪下载事件
    adjustTracking.trackDownload({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackMacAppleDownload({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownload({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadOnly({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadMacApple({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(macArmResource.url, "mac_arm");
  };

  // Mac Intel下载处理
  const handleMacIntelDownload = () => {
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    if (!macIntelResource) {
      console.error("Mac Intel resource not available");
      return;
    }

    // 追踪下载事件
    adjustTracking.trackDownload({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackMacIntelDownload({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownload({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadOnly({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadMacIntel({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(macIntelResource.url, "mac_intel");
  };

  // Windows下载处理
  const handleWindowsDownload = () => {
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    if (!windowsResource) {
      console.error("Windows resource not available");
      return;
    }

    // 追踪下载事件
    adjustTracking.trackDownload({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackWindowsDownload({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownload({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadOnly({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackSubDownloadWindows({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(windowsResource.url, "windows");
  };

  // 动画配置
  const transition: Transition = {
    duration: 0.35,
    ease: [0.59, 0, 0.35, 1],
  };

  const enteringState: TargetAndTransition = {
    rotateX: 0,
    skewY: 0,
    scaleY: 1,
    scaleX: 1,
    y: 0,
    transition: {
      ...transition,
      y: { type: "spring", visualDuration: 0.7, bounce: 0.2 },
    },
  };

  const exitingState = {
    rotateX: -5,
    skewY: -1.5,
    scaleY: 2,
    scaleX: 0.4,
    y: 100,
  };

  return (
    <div className={styles.overlayRoot} onClick={close}>
      <motion.div
        className={styles.overlayContent}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={transition}
      >
        <motion.div
          className={styles.downloadConfirmation}
          onClick={(e) => e.stopPropagation()}
          initial={exitingState}
          animate={enteringState}
          exit={exitingState}
          transition={transition}
          style={{
            transformPerspective: 1000,
            originX: 0.5,
            originY: 0,
          }}
        >
          <div className={styles.downloadConfirmationHeader}>
            <h2 className={styles.overlayTitle}>Download Fellou</h2>
            <p className={styles.overlayDescription}>Choose your platform to start browsing with an AI assistant</p>
          </div>

          <div className={styles.controls}>
            <button
              onClick={handleMacArmDownload}
              className={`${styles.platformButton} ${
                !macInfo?.isEnabled || !macArmResource ? styles.platformButtonDisabled : ""
              }`}
              disabled={!macInfo?.isEnabled || !macArmResource}
            >
              <img
                src="https://api.iconify.design/logos:apple.svg"
                alt="Apple"
                className={`${styles.platformIcon} ${
                  !macInfo?.isEnabled || !macArmResource ? styles.platformIconDisabled : ""
                }`}
              />
              {macArmResource?.text || "macOS (Apple Silicon)"}
            </button>

            <button
              onClick={handleMacIntelDownload}
              className={`${styles.platformButton} ${
                !macInfo?.isEnabled || !macIntelResource ? styles.platformButtonDisabled : ""
              }`}
              disabled={!macInfo?.isEnabled || !macIntelResource}
            >
              <img
                src="https://api.iconify.design/logos:apple.svg"
                alt="Apple"
                className={`${styles.platformIcon} ${
                  !macInfo?.isEnabled || !macIntelResource ? styles.platformIconDisabled : ""
                }`}
              />
              {macIntelResource?.text || "macOS (Intel)"}
            </button>

            <button onClick={handleWindowsDownload} className={styles.platformButton} disabled={!windowsResource}>
              <img
                src="https://api.iconify.design/logos:microsoft-icon.svg"
                alt="Microsoft"
                className={`${styles.platformIcon} ${
                  !windowsInfo?.isEnabled || !windowsResource ? styles.platformIconDisabled : ""
                }`}
              />
              <div className={styles.windowsButtonContent}>
                <span>{windowsResource?.overlayTitle || "Windows"}</span>
              </div>
            </button>
          </div>

          <button onClick={close} className={styles.cancelButton}>
            Close
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default InfoOverlay;
